import { pack } from '@attain-apps/data-utils'
import * as store from '@attain-apps/fe/web/main/lib/store'

import * as ecModels from '../../../models'
import * as plugin from '../../plugin'

import { getActionsConfig } from './actions'
import { Config } from './config'
import * as effects from './effects'
import type * as models from './models'
import { Selectors } from './selectors'
import { State } from './state'

import AppliedStorage = store.storage.included.SessionStorage
import EditedStorage = store.storage.included.InMemoryStorage

/**
 * TODO: documentation
 */
export class Plugin<
  TTypeParams extends ecModels.TypeParams.Constraint,
> extends plugin.Plugin<
  TTypeParams,
  plugin.Plugin.Id.Standard.Selection,
  State,
  Selectors<TTypeParams>,
  ReturnType<typeof getActionsConfig<TTypeParams>>
> {
  /**
   * Shared configuration for {@link applied} and {@link edited}
   */
  private readonly _storageConfig = {
    parent: this.feature,
    properties: {
      selectionLookup: store.storage.lookup({
        defaultKey: ecModels.Target.DEFAULT,
        get: (key, ids = []) => ids,
        set: (
          key: ecModels.Target,
          // This allows us to do a more command based patching system
          value: models.Instruction,
          current: readonly ecModels.EntityId[] = [],
        ) => {
          if (value.add) {
            // Selection should be everything in the current list plus the id(s)
            // to remove
            return [...new Set([...current, ...pack(value.add)])]
          }
          if (value.remove) {
            // Selection should be everything in the current list minus the id(s)
            // to remove
            const removeable = new Set(pack(value.remove))
            return current.filter(id => !removeable.has(id))
          }
          if (value.set) {
            // Selection should be exactly the given id(s), but as a list
            return pack(value.set)
          }
          if (value.release) {
            // Selection should be removed entirely
            return undefined
          }
          return current
        },
      }),
    },
  } satisfies
    | AppliedStorage.constructor.Params<store.storage.PropertiesConfig.Constraint>
    | EditedStorage.constructor.Params<store.storage.PropertiesConfig.Constraint>

  /**
   * These are the selectors and actions for the "official" selections
   */
  public readonly applied = new AppliedStorage({
    ...this._storageConfig,
    name: 'Applied',
  })
  /**
   * These are the selectors and actions for "proposed" selections
   */
  public readonly edited = new EditedStorage({
    ...this._storageConfig,
    name: 'Edited',
  })
  /**
   * @see {@link Config}
   */
  public readonly config: Readonly<Config<TTypeParams>>

  constructor({ collection, config }: Plugin.constructor.Params<TTypeParams>) {
    super({
      actions: getActionsConfig<TTypeParams>(),
      collection,
      id: plugin.Plugin.Id.Standard.Selection,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })
    this.config = Config.from(config)

    this.feature.registerEffects(
      effects.applyEdits(this),
      effects.discardEdits(this),
      effects.getEntities(this),
      effects.onChangeInCurrentList(this),
    )
  }
}
export namespace Plugin {
  export namespace constructor {
    export type Params<TTypeParams extends ecModels.TypeParams.Constraint> =
      Pick<plugin.Plugin.Default<TTypeParams>, 'collection'> & {
        config?: Config.Options<TTypeParams>
      }
  }
}
